import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, CalendarClock, ChevronRight, PackageMinus, PackagePlus } from "lucide-react";
import { DeliveryOrder } from "@/features/delivery/api/deliveryApi";
import { formatDeliveryStatus } from "@/features/delivery/lib/deliveryCalculations.js";
import { clearDriverToken, getDriverOrders, getDriverToken, isUnauthenticated } from "@/features/driver/api/driverApi";
import { formatScheduledDateTime, formatStationName } from "@/features/driver/lib/driverPresentation.js";
import DriverMenu from "@/features/driver/components/DriverMenu";
import { DriverLanguageProvider, useDriverLanguage } from "@/features/driver/i18n/useDriverLanguage";
import { getRegionConfig } from "@/lib/regionConfig";

const statusBadgeClass = (status: string): string => {
  const map: Record<string, string> = {
    completed: "bg-green-100 text-green-800",
    pending: "bg-red-100 text-red-700",
    cancelled: "bg-neutral-200 text-neutral-700",
    no_show: "bg-orange-100 text-orange-700",
  };

  return map[status] ?? "bg-amber-100 text-amber-800";
};

export default function DriverDashboard() {
  return (
    <DriverLanguageProvider>
      <DriverDashboardContent />
    </DriverLanguageProvider>
  );
}

function DriverDashboardContent() {
  const navigate = useNavigate();
  const region = getRegionConfig();
  const { t } = useDriverLanguage();
  const [orders, setOrders] = useState<DeliveryOrder[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!getDriverToken()) {
      navigate("/driver/login");
      return;
    }

    getDriverOrders()
      .then((result) => setOrders(result.orders))
      .catch((err) => {
        if (isUnauthenticated(err)) {
          clearDriverToken();
          navigate("/driver/login");
          return;
        }
        setError(err?.message ?? t("defaultOrdersError"));
      });
  }, [navigate, t]);

  const openOrder = (id: number) => navigate(`/driver/order/${id}`);

  return (
    <main className="min-h-[100dvh] overflow-x-hidden bg-neutral-100">
      <header className="sticky top-0 z-10 bg-europcarGreen px-4 pb-4 text-white [padding-top:max(1rem,env(safe-area-inset-top))]">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-lg font-bold tracking-tight">Europcar</span>
            <span
              className="shrink-0 rounded bg-black/20 px-2 py-0.5 text-xs font-semibold"
              title={region.countryName}
            >
              {region.code}
            </span>
          </div>
          <DriverMenu />
        </div>
        <div className="mx-auto mt-3 max-w-4xl">
          <h1 className="text-xl font-bold">{t("assignedJobsTitle")}</h1>
          <p className="text-sm font-medium text-white/90">{t("assignedJobsSubtitle")}</p>
        </div>
      </header>

      <section className="mx-auto max-w-4xl px-4 py-5">
        {error && <p className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</p>}

        {orders.length > 0 ? (
          <ul className="space-y-3">
            {orders.map((order) => {
              const JobIcon = order.type === "pickup" ? PackagePlus : PackageMinus;

              return (
                <li key={order.id}>
                  <button
                    type="button"
                    onClick={() => openOrder(order.id)}
                    className="flex w-full min-w-0 cursor-pointer items-center gap-3 rounded-xl border border-neutral-200 bg-white p-4 text-left shadow-sm transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
                  >
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${
                        order.type === "pickup" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"
                      }`}
                    >
                      <JobIcon className="h-5 w-5" aria-hidden="true" />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-semibold text-neutral-900">
                          {order.customer_display_name ?? "—"}
                        </span>
                        <span className={`shrink-0 rounded-md px-2 py-1 text-xs font-medium ${statusBadgeClass(order.status)}`}>
                          {formatDeliveryStatus(order.status)}
                        </span>
                      </span>
                      <span className="mt-1 block truncate text-sm text-neutral-600">
                        {order.type === "pickup" ? t("pickupLabel") : t("returnLabel")} · {formatScheduledDateTime(order.scheduled_at)}
                      </span>
                      <span className="mt-1.5 flex min-w-0 items-center gap-1.5 text-xs font-medium text-neutral-600">
                        <Building2 className="h-3.5 w-3.5 shrink-0 text-green-700" aria-hidden="true" />
                        <span className="min-w-0 truncate">
                          {order.type === "pickup" ? t("pickupStationLabel") : t("returnStationLabel")}:{" "}
                          <span className="text-neutral-800">
                            {formatStationName(order.station) || t("stationUnavailable")}
                          </span>
                        </span>
                      </span>
                    </span>

                    <ChevronRight className="h-5 w-5 shrink-0 text-neutral-400" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          !error && (
            <div className="rounded-xl border border-dashed border-neutral-300 bg-white p-8 text-center text-neutral-600">
              <CalendarClock className="mx-auto mb-3 h-8 w-8" aria-hidden="true" />
              {t("noAssignedJobs")}
            </div>
          )
        )}
      </section>
    </main>
  );
}
